import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarClock,
  ChevronDown,
  Factory,
  FileDown,
  Loader2,
  Mail,
  Plus,
  Send,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { API, fmtError } from "@/lib/auth";

const fmt = (n) =>
  `${n < 0 ? "−" : ""}₺${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(Math.abs(n))}`;

const trDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60";

export const TYPE_META = {
  musteri: {
    label: "Müşteri",
    balanceLabel: "alacak",
    icon: UserRound,
    cls: "text-emerald-400",
    chip: "border-emerald-400/30 bg-emerald-500/10 text-emerald-300",
  },
  tedarikci: {
    label: "Tedarikçi",
    balanceLabel: "borç",
    icon: Factory,
    cls: "text-amber-400",
    chip: "border-amber-400/30 bg-amber-500/10 text-amber-300",
  },
};

const BalanceTag = ({ contact }) => {
  const meta = TYPE_META[contact.type];
  if (contact.balance === 0)
    return (
      <span className="font-mono-num text-sm text-slate-500">kapalı · ₺0</span>
    );
  if (contact.balance < 0)
    return (
      <span className="font-mono-num text-sm font-semibold text-sky-300">
        avans {fmt(Math.abs(contact.balance))}
      </span>
    );
  return (
    <span className={`font-mono-num text-sm font-semibold ${meta.cls}`}>
      {meta.balanceLabel} {fmt(contact.balance)}
    </span>
  );
};

const Ledger = ({ contactId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/contacts/${contactId}/ledger`)
      .then((r) => setData(r.data))
      .catch(() => setData({ upcoming: [], transactions: [] }));
  }, [contactId]);
  if (!data)
    return <div className="py-3 text-xs text-slate-600">Yükleniyor…</div>;
  return (
    <div
      data-testid={`contact-ledger-${contactId}`}
      className="grid gap-4 border-t border-white/5 pt-4 sm:grid-cols-2"
    >
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          Planlanan
        </div>
        <ul className="space-y-1.5 text-xs">
          {data.upcoming.map((u) => (
            <li
              key={u.id}
              className={`flex items-center justify-between gap-2 ${u.done ? "text-slate-600 line-through" : "text-slate-300"}`}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <CalendarClock className="h-3 w-3 shrink-0" />
                <span className="truncate">{u.title}</span>
              </span>
              <span className="font-mono-num shrink-0">
                {trDate(u.due_date)} · {fmt(u.amount)}
              </span>
            </li>
          ))}
          {data.upcoming.length === 0 && (
            <li className="text-slate-600">Planlanan kayıt yok.</li>
          )}
        </ul>
      </div>
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          Gerçekleşen
        </div>
        <ul className="space-y-1.5 text-xs">
          {data.transactions.map((t) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-2 text-slate-300"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {t.type === "gelir" ? (
                  <ArrowUpRight className="h-3 w-3 shrink-0 text-emerald-400" />
                ) : (
                  <ArrowDownLeft className="h-3 w-3 shrink-0 text-red-400" />
                )}
                <span className="truncate">{t.description || t.category}</span>
              </span>
              <span className="font-mono-num shrink-0">
                {trDate(t.date)} · {fmt(t.amount)}
              </span>
            </li>
          ))}
          {data.transactions.length === 0 && (
            <li className="text-slate-600">Henüz hareket yok.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const StatementActions = ({ contact, run }) => {
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState("");
  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/contacts/${contact.id}/send-statement`,
      );
      toast.success(`Ekstre ${data.to} adresine PDF olarak gönderildi.`);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  const saveEmail = (e) => {
    e.preventDefault();
    run(
      () =>
        axios.patch(`${API}/contacts/${contact.id}`, {
          name: contact.name,
          type: contact.type,
          email,
          phone: contact.phone || "",
          note: contact.note || "",
        }),
      "E-posta kaydedildi",
    );
  };
  return (
    <div
      data-testid={`statement-actions-${contact.id}`}
      className="mb-4 flex flex-wrap items-center gap-2"
    >
      <a
        data-testid={`statement-pdf-${contact.id}`}
        href={`${API}/contacts/${contact.id}/statement.pdf`}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-emerald-400/50 hover:text-emerald-300"
      >
        <FileDown className="h-3.5 w-3.5" /> Ekstre PDF
      </a>
      {contact.email ? (
        <button
          data-testid={`statement-send-${contact.id}`}
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 py-1.5 text-xs font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
        >
          {sending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}{" "}
          Ekstreyi E-postala
        </button>
      ) : (
        <form
          data-testid={`statement-email-form-${contact.id}`}
          onSubmit={saveEmail}
          className="flex items-center gap-2"
        >
          <input
            data-testid={`statement-email-input-${contact.id}`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Ekstre için e-posta ekle"
            className="rounded-full border border-white/12 bg-[#07090E] px-3.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60"
          />
          <button
            data-testid={`statement-email-save-${contact.id}`}
            type="submit"
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition-all duration-300 hover:bg-emerald-500/20"
          >
            <Mail className="h-3.5 w-3.5" /> Kaydet
          </button>
        </form>
      )}
    </div>
  );
};

export const ContactsTab = ({ contacts, busy, run }) => {
  const [form, setForm] = useState({
    name: "",
    type: "musteri",
    email: "",
    phone: "",
    note: "",
  });
  const [openId, setOpenId] = useState(null);

  const add = (e) => {
    e.preventDefault();
    run(() => axios.post(`${API}/contacts`, form), "Cari hesap eklendi").then(
      () =>
        setForm({ name: "", type: "musteri", email: "", phone: "", note: "" }),
    );
  };

  const totals = contacts.reduce(
    (acc, c) => {
      if (c.balance > 0) acc[c.type] += c.balance;
      return acc;
    },
    { musteri: 0, tedarikci: 0 },
  );

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <form
        data-testid="add-contact-form"
        onSubmit={add}
        className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-2"
      >
        <h3 className="font-display mb-5 flex items-center gap-2 text-base font-semibold text-slate-100">
          <Plus className="h-4 w-4 text-emerald-400" /> Yeni Cari Hesap
        </h3>
        <div className="mb-4 grid grid-cols-2 gap-2">
          {Object.entries(TYPE_META).map(([t, meta]) => (
            <button
              key={t}
              type="button"
              data-testid={`contact-type-${t}`}
              onClick={() => setForm({ ...form, type: t })}
              className={`rounded-xl border py-2.5 text-sm font-semibold transition-all duration-300 ${
                form.type === t
                  ? t === "musteri"
                    ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-300"
                    : "border-amber-400/60 bg-amber-500/15 text-amber-300"
                  : "border-white/12 text-slate-500 hover:text-slate-300"
              }`}
            >
              {meta.label}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          <input
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Firma / kişi adı"
            className={inputCls}
          />
          <input
            data-testid="contact-email-input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="E-posta (isteğe bağlı)"
            className={inputCls}
          />
          <input
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Telefon (isteğe bağlı)"
            className={inputCls}
          />
          <input
            data-testid="contact-note-input"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Not (vergi no, ödeme koşulu…)"
            className={inputCls}
          />
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Cari Hesabı Ekle
          </button>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-600">
          Yaklaşan bir tahsilat veya ödemeyi cariye bağladığında bakiye otomatik
          oluşur; tamamlandı işaretlediğinde gerçek işlem kaydedilir ve bakiye
          kapanır.
        </p>
      </form>

      <div className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-3">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-base font-semibold text-slate-100">
            Cari Hesaplar
          </h3>
          <div className="flex items-center gap-4 text-xs">
            <span
              data-testid="contacts-total-receivable"
              className="font-mono-num text-emerald-400"
            >
              alacak {fmt(totals.musteri)}
            </span>
            <span
              data-testid="contacts-total-payable"
              className="font-mono-num text-amber-400"
            >
              borç {fmt(totals.tedarikci)}
            </span>
          </div>
        </div>
        <ul data-testid="contacts-list" className="divide-y divide-white/5">
          {contacts.map((c) => {
            const meta = TYPE_META[c.type];
            const open = openId === c.id;
            return (
              <li
                key={c.id}
                data-testid={`contact-row-${c.id}`}
                className="group py-3.5"
              >
                <div className="flex items-center justify-between gap-3">
                  <button
                    data-testid={`contact-toggle-${c.id}`}
                    onClick={() => setOpenId(open ? null : c.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${c.type === "musteri" ? "bg-emerald-500/10" : "bg-amber-500/10"}`}
                    >
                      <meta.icon className={`h-4 w-4 ${meta.cls}`} />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-100">
                        {c.name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.chip}`}
                        >
                          {meta.label}
                        </span>
                        {c.open_count > 0 && (
                          <span>{c.open_count} açık kayıt</span>
                        )}
                        {c.email && (
                          <span className="hidden truncate sm:inline">
                            · {c.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-3">
                    <BalanceTag contact={c} />
                    <ChevronDown
                      className={`h-4 w-4 text-slate-600 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    />
                    <button
                      data-testid={`contact-delete-${c.id}`}
                      onClick={() =>
                        run(
                          () => axios.delete(`${API}/contacts/${c.id}`),
                          "Cari hesap silindi",
                        )
                      }
                      className="text-slate-600 opacity-0 transition-all duration-300 hover:text-red-400 group-hover:opacity-100"
                      aria-label="Cari hesabı sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 pl-12">
                        {c.note && (
                          <p className="mb-3 text-xs text-slate-500">
                            {c.note}
                          </p>
                        )}
                        <StatementActions contact={c} run={run} />
                        <Ledger
                          key={`${c.id}-${c.planned}-${c.realised}`}
                          contactId={c.id}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
          {contacts.length === 0 && (
            <li className="py-8 text-center text-sm text-slate-600">
              Henüz cari hesap yok — ilk müşteri veya tedarikçini ekle.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};
